import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { TmsOperationTypeEnum, TmsRouteTypeEnum } from '../../../../../../enums/TmsEnum';
import { SiteType } from '../../../../../../graphql/types/SiteType';
import type { TmsTaskDetailType, TmsTaskWaypointType } from '../../../graphql/queries/getTmsTaskDetail';
import {
    GET_LOADING_ORDER_DETAIL_WAYPOINT_PARCEL_COUNTS_BY_TASK_ID,
    type GetLoadingOrderDetailWaypointParcelCountsByTaskIdResponse,
    type GetLoadingOrderDetailWaypointParcelCountsByTaskIdVariables,
} from '../../../graphql/queries/getLoadingOrderDetailsByTaskId';
import {
    GET_UNLOADING_ORDER_DETAIL_WAYPOINT_PARCEL_COUNTS_BY_TASK_ID,
    type GetUnloadingOrderDetailWaypointParcelCountsByTaskIdResponse,
    type GetUnloadingOrderDetailWaypointParcelCountsByTaskIdVariables,
} from '../../../graphql/queries/getUnloadingOrderDetailsByTaskId';
import {
    GET_TMS_WAREHOUSE_LIST,
    type GetTmsWarehouseListResponse,
    type TmsWarehouseOption,
} from '../../../../../../graphql/queries/getWarehouseList';
import type { ContainerInfoRow, ContainerUnitType } from '../components/ContainerInfoTable';

/** 后端 containerType 与前端单位枚举对应：1-卡板箱 2-蛇皮袋 3-托盘 4-Gaylord */
export const CONTAINER_TYPE_TO_UNIT: Record<number, ContainerUnitType> = {
    1: 'PALLET_BOX',
    2: 'BULK_BAG',
    3: 'PALLET',
    4: 'GAYLORD',
};

/** 前端单位到后端 containerType */
export const UNIT_TO_CONTAINER_TYPE: Record<ContainerUnitType, number> = {
    PALLET_BOX: 1,
    BULK_BAG: 2,
    PALLET: 3,
    GAYLORD: 4,
};

export interface UseContainerInfoRenderOptions {
    waypoint: TmsTaskWaypointType;
    task?: TmsTaskDetailType;
    containerSource: TmsOperationTypeEnum;
}

/**
 * 容器信息（目的地、数量、单位）及运单数量的数据与选项
 * Loading 与 Unloading 共用；parcelCount 按 containerSource 分别请求装车/卸车运单数量
 */
export function useContainerInfoRender({ waypoint, task, containerSource }: UseContainerInfoRenderOptions) {
    const { data: warehousesData } = useQuery<GetTmsWarehouseListResponse>(GET_TMS_WAREHOUSE_LIST, {
        skip: !task,
    });

    const warehouseOptions: TmsWarehouseOption[] = useMemo(() => {
        const list = warehousesData?.warehouses ?? [];
        return list.map((w) => ({
            id: w.id,
            sortingCode: w.sortingCode ?? w.id,
            siteType: w.siteType,
            parentSiteId: w.parentSiteId,
        }));
    }, [warehousesData]);

    const { data: loadingOrderDetailCountsData, loading: loadingOrderDetailCountsLoading } = useQuery<
        GetLoadingOrderDetailWaypointParcelCountsByTaskIdResponse,
        GetLoadingOrderDetailWaypointParcelCountsByTaskIdVariables
    >(GET_LOADING_ORDER_DETAIL_WAYPOINT_PARCEL_COUNTS_BY_TASK_ID, {
        variables: { taskId: waypoint.taskId },
        skip: containerSource !== TmsOperationTypeEnum.Loading,
    });

    const { data: unloadingOrderDetailCountsData, loading: unloadingOrderDetailCountsLoading } = useQuery<
        GetUnloadingOrderDetailWaypointParcelCountsByTaskIdResponse,
        GetUnloadingOrderDetailWaypointParcelCountsByTaskIdVariables
    >(GET_UNLOADING_ORDER_DETAIL_WAYPOINT_PARCEL_COUNTS_BY_TASK_ID, {
        variables: { taskId: waypoint.taskId },
        skip: containerSource !== TmsOperationTypeEnum.Unloading,
    });

    const loadingWaypointParcelCounts = useMemo(() => {
        const data = loadingOrderDetailCountsData?.getLoadingOrderDetailWaypointParcelCountsByTaskId || [];
        const counts = new Map<number, number>();
        data.forEach((item) => {
            counts.set(parseInt(String(item.waypointId)), item.parcelCount);
        });
        return counts;
    }, [loadingOrderDetailCountsData]);

    const unloadingWaypointParcelCounts = useMemo(() => {
        const data = unloadingOrderDetailCountsData?.getUnloadingOrderDetailWaypointParcelCountsByTaskId || [];
        const counts = new Map<number, number>();
        data.forEach((item) => {
            counts.set(parseInt(String(item.waypointId)), item.parcelCount);
        });
        return counts;
    }, [unloadingOrderDetailCountsData]);

    const taskWaypointId = Number(waypoint.id);
    const parcelCount =
        containerSource === TmsOperationTypeEnum.Loading
            ? loadingOrderDetailCountsLoading
                ? null
                : (loadingWaypointParcelCounts.get(taskWaypointId) ?? 0)
            : containerSource === TmsOperationTypeEnum.Unloading
              ? unloadingOrderDetailCountsLoading
                ? null
                : (unloadingWaypointParcelCounts.get(taskWaypointId) ?? 0)
              : undefined;

    const containerInfoRows: ContainerInfoRow[] = useMemo(() => {
        const info = waypoint.containerInfo;
        if (!info || info.length === 0) return [];
        const rows: ContainerInfoRow[] = [];
        let keyIndex = 0;
        const keyPrefix = containerSource === TmsOperationTypeEnum.Loading ? 'container' : 'unload';
        info.filter((g) => g.containerSource === containerSource).forEach((group) => {
            const dest = String(group.destinationWarehouseId);
            group.containerTypes.forEach((ct) => {
                rows.push({
                    key: `${keyPrefix}-${keyIndex++}`,
                    destination: dest,
                    quantity: ct.containerCount,
                    unit: CONTAINER_TYPE_TO_UNIT[ct.containerType] ?? 'PALLET_BOX',
                });
            });
        });
        return rows;
    }, [containerSource, waypoint.containerInfo]);

    const containerDestinationOptions = useMemo(() => {
        if (task?.routeType !== undefined && warehouseOptions.length > 0) {
            const routeType = task.routeType;
            const hubId = task.warehouseId != null ? String(task.warehouseId) : null;
            let filtered: TmsWarehouseOption[];
            if (routeType === TmsRouteTypeEnum.Linehaul) {
                filtered = warehouseOptions.filter((w) => w.siteType === SiteType.HUB);
            } else if (routeType === TmsRouteTypeEnum.Feeder && hubId) {
                filtered = warehouseOptions.filter(
                    (w) =>
                        (w.siteType === SiteType.WAREHOUSE && w.parentSiteId === hubId) ||
                        (w.siteType === SiteType.HUB && w.id === hubId)
                );
            } else {
                filtered = warehouseOptions;
            }
            return filtered.map((w) => ({ value: w.id, label: w.sortingCode ?? w.id }));
        }
        const info = waypoint.containerInfo;
        if (!info) return [];
        const byId = new Map<string, string>();
        info.filter((g) => g.containerSource === containerSource).forEach((g) => {
            const id = String(g.destinationWarehouseId);
            if (byId.has(id)) return;
            const label = g.destinationWarehouseSortingCode ?? id;
            byId.set(id, label);
        });
        return Array.from(byId.entries()).map(([value, label]) => ({ label, value }));
    }, [containerSource, task?.routeType, task?.warehouseId, warehouseOptions, waypoint.containerInfo]);

    const warehouseIdBySortingCode = useMemo(() => {
        const map = new Map<string, number>();
        warehouseOptions.forEach((w) => {
            const code = String(w.sortingCode ?? '').trim();
            const idNum = Number(w.id);
            if (!code || !Number.isFinite(idNum)) return;
            map.set(code, idNum);
        });
        return map;
    }, [warehouseOptions]);

    return { containerInfoRows, containerDestinationOptions, parcelCount, warehouseIdBySortingCode };
}
